import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { Info } from 'lucide-react';
import { formatUSD, formatPct, formatNum } from './ops_helpers';

/**
 * KPI Card profesional con tooltip explicativo.
 * tone: success | danger | warning | neutral | violet
 */
export const KpiCard = ({ label, value, hint, tone = 'neutral', tooltip, testId, icon: Icon }) => {
  const toneClass = {
    neutral: 'text-zinc-100',
    success: 'text-emerald-400',
    danger:  'text-rose-400',
    warning: 'text-amber-400',
    violet:  'text-violet-300',
  }[tone] || 'text-zinc-100';

  const accent = {
    neutral: 'border-zinc-800',
    success: 'border-emerald-700/40',
    danger:  'border-rose-700/40',
    warning: 'border-amber-700/40',
    violet:  'border-violet-700/40',
  }[tone] || 'border-zinc-800';

  return (
    <Card
      data-testid={testId}
      className={`bg-zinc-900/60 border ${accent} hover:border-violet-500/40 transition-colors`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium leading-tight">
            {label}
          </p>
          {tooltip ? (
            <TooltipProvider delayDuration={120}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <button className="text-zinc-600 hover:text-violet-300 transition-colors" aria-label="info">
                    <Info className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] text-xs bg-zinc-950 border-zinc-700">
                  {tooltip}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          ) : Icon ? <Icon className="w-3.5 h-3.5 text-zinc-600" /> : null}
        </div>
        <p className={`mt-2 text-xl font-bold ${toneClass} truncate`} title={value}>{value}</p>
        {hint && <p className="text-[10px] text-zinc-500 mt-1 truncate">{hint}</p>}
      </CardContent>
    </Card>
  );
};

/**
 * Grid completo de KPIs profesionales para un bot.
 * Props: analytics (objeto devuelto por /api/ops/bots/{bot_id}/analytics)
 */
export const AnalyticsKpiGrid = ({ analytics }) => {
  if (!analytics) return null;

  const pnlTone = analytics.net_pnl >= 0 ? 'success' : 'danger';
  const pf = analytics.profit_factor;
  const pfTone = pf == null ? 'neutral' : pf >= 1.5 ? 'success' : pf >= 1.0 ? 'warning' : 'danger';
  const wrTone = analytics.win_rate >= 50 ? 'success' : analytics.win_rate >= 40 ? 'warning' : 'danger';
  const ddTone = analytics.max_drawdown_pct >= 15 ? 'danger' : analytics.max_drawdown_pct >= 8 ? 'warning' : 'success';
  const sharpeTone = analytics.sharpe >= 1.5 ? 'success' : analytics.sharpe >= 0.8 ? 'warning' : 'danger';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
      <KpiCard
        testId="kpi-net-pnl"
        label="Net PnL"
        value={formatUSD(analytics.net_pnl)}
        hint={`${analytics.trades_count} trades`}
        tone={pnlTone}
        tooltip="Beneficio neto (gross profit menos gross loss) en el periodo seleccionado."
      />
      <KpiCard
        testId="kpi-win-rate"
        label="Win Rate"
        value={formatPct(analytics.win_rate, 1)}
        hint={`${analytics.wins}W / ${analytics.losses}L`}
        tone={wrTone}
        tooltip="% de operaciones ganadoras. Por sí solo no indica rentabilidad: un WR bajo con R:R alto puede ser muy rentable."
      />
      <KpiCard
        testId="kpi-profit-factor"
        label="Profit Factor"
        value={pf == null ? '∞' : formatNum(pf, 2)}
        hint={`PF = GrossProfit / GrossLoss`}
        tone={pfTone}
        tooltip="Cociente entre dinero ganado y dinero perdido. >1.5 = sistema saludable. >2 = excelente."
      />
      <KpiCard
        testId="kpi-expectancy"
        label="Expectancy"
        value={formatUSD(analytics.expectancy)}
        hint="Por trade promedio"
        tone={analytics.expectancy >= 0 ? 'success' : 'danger'}
        tooltip="Cuánto esperar ganar (o perder) por cada trade en promedio. Net PnL / trades."
      />
      <KpiCard
        testId="kpi-avg-r"
        label="Avg R"
        value={formatNum(analytics.avg_r, 2)}
        hint={`${analytics.r_multiples_count} con SL`}
        tone={analytics.avg_r >= 0.3 ? 'success' : analytics.avg_r >= 0 ? 'warning' : 'danger'}
        tooltip="R-múltiplo medio: pnl / riesgo asumido al entrar. >0.3R es un sistema robusto."
      />
      <KpiCard
        testId="kpi-rr-real"
        label="R:R real"
        value={formatNum(analytics.rr_real, 2)}
        hint={`AvgWin/AvgLoss`}
        tone={analytics.rr_real >= 1.5 ? 'success' : analytics.rr_real >= 1.0 ? 'warning' : 'danger'}
        tooltip="Reward-to-Risk efectivo. Cuánto gana en promedio una operación ganadora vs lo que pierde una perdedora."
      />
      <KpiCard
        testId="kpi-avg-win"
        label="Avg Win"
        value={formatUSD(analytics.avg_win)}
        tone="success"
        tooltip="Promedio de las operaciones ganadoras."
      />
      <KpiCard
        testId="kpi-avg-loss"
        label="Avg Loss"
        value={formatUSD(analytics.avg_loss)}
        tone="danger"
        tooltip="Promedio (en valor absoluto) de las operaciones perdedoras."
      />
      <KpiCard
        testId="kpi-max-dd"
        label="Max Drawdown"
        value={formatUSD(analytics.max_drawdown_abs)}
        hint={formatPct(analytics.max_drawdown_pct, 2)}
        tone={ddTone}
        tooltip="Mayor caída desde un pico hasta un valle en la curva PnL. <10% es saludable."
      />
      <KpiCard
        testId="kpi-sharpe"
        label="Sharpe"
        value={formatNum(analytics.sharpe, 2)}
        hint="Anualizado"
        tone={sharpeTone}
        tooltip="Ratio Sharpe anualizado: media de returns / desviación estándar × √252. >1 = bueno, >2 = excelente."
      />
      <KpiCard
        testId="kpi-sortino"
        label="Sortino"
        value={formatNum(analytics.sortino, 2)}
        hint="Solo vol. negativa"
        tone={analytics.sortino >= 2 ? 'success' : analytics.sortino >= 1 ? 'warning' : 'danger'}
        tooltip="Ratio Sortino: como Sharpe pero penaliza únicamente la volatilidad a la baja. Más realista para sistemas direccionales."
      />
      <KpiCard
        testId="kpi-streaks"
        label="Racha W / L"
        value={`${analytics.max_win_streak} / ${analytics.max_loss_streak}`}
        hint="Wins / Losses consec."
        tone="violet"
        tooltip="Máximas rachas consecutivas de operaciones ganadoras (W) y perdedoras (L) en el periodo."
      />
    </div>
  );
};
